/**
 * The zod schema for `AgentRuntimeConfig` — **the ONLY validator** (design 12.4, Seam A).
 *
 * P-FACTORY's build step ends with `RuntimeConfigSchema.parse(document)` and fails the build
 * if it does not pass. P-RUNTIME's cache loader begins with the same call and rejects the
 * call if it does not pass. Neither side may widen the schema without the other's test
 * failing, because both tests read `./runtime-config.fixture.json`.
 *
 * `.strict()` everywhere: an unknown key in a config document is a producer/consumer version
 * skew, and skew must fail loudly rather than be dropped.
 *
 * The document is stored in `agent_version.config_document` in exactly this (camelCase)
 * shape. The snake_case convention of design 5.1 governs the public API surface, not this
 * internal document.
 *
 * AMENDED 2026-10-19 (plan-021 DOM, ADR 0074 proposed, design §33.4.2, §33.4.6): `voiceAgentPass`, the pass target list,
 * strict like every sub-object. REQUIRED since plan-021 CONTRACT (Release 2): Release 1 read a document without it as no
 * targets; migration 0168 back-filled every built document (Window B, production count 0), so that read is gone and a
 * document without the key fails to parse like any other unfinished build.
 */
import { z } from "zod";
import { MEDIUM } from "../conversation/medium.js";
import { idSchema } from "../id/schema.js";
import { accountId, agentVersionId, connectionId, voiceAgentId, voiceId, workspaceId, } from "../id/codec.js";
import { deepFreezeRuntimeConfig } from "./runtime-config.js";
import { SINGLE_LINE_WITHOUT_CONTROL_STRING, VOICE_AGENT_PASS_DISPLAY_NAME_CHARACTER_LIMIT, VOICE_AGENT_PASS_MODE, VOICE_AGENT_PASS_NOTE_CHARACTER_LIMIT, VOICE_AGENT_PASS_TARGET_KEY, VOICE_AGENT_PASS_TARGET_LIMIT, VOICE_AGENT_PASS_WHEN_CHARACTER_LIMIT, } from "./voice-agent-pass.js";
/**
 * Format checks are written as regexes rather than with zod's URL / UUID helpers, matching
 * `packages/config`'s reasoning: those helpers have moved between zod majors, and this schema
 * is the single validator two separate units compile against.
 */
const HTTPS_URL = /^https?:\/\/[^\s/$.?#][^\s]*$/;
const LanguageCodeSchema = z
    .string()
    .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, "not a BCP-47 language code");
const TimeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "not HH:MM");
const CalendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "not YYYY-MM-DD");
const OpeningIntervalSchema = z
    .object({ opensAt: TimeOfDaySchema, closesAt: TimeOfDaySchema })
    .strict();
const OpeningHoursExceptionSchema = z
    .object({
    date: CalendarDateSchema,
    label: z.string().min(1),
    interval: z.array(OpeningIntervalSchema),
})
    .strict();
const OpeningHoursSchema = z
    .object({
    timeZone: z.string().min(1),
    week: z
        .object({
        monday: z.array(OpeningIntervalSchema),
        tuesday: z.array(OpeningIntervalSchema),
        wednesday: z.array(OpeningIntervalSchema),
        thursday: z.array(OpeningIntervalSchema),
        friday: z.array(OpeningIntervalSchema),
        saturday: z.array(OpeningIntervalSchema),
        sunday: z.array(OpeningIntervalSchema),
    })
        .strict(),
    exception: z.array(OpeningHoursExceptionSchema),
})
    .strict();
const OverflowBehaviourSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("queued"),
        queuePhraseKey: z.string().min(1),
        maxWaitMs: z.number().int().nonnegative(),
    })
        .strict(),
    z.object({ kind: z.literal("message_taken"), promptPhraseKey: z.string().min(1) }).strict(),
    z.object({ kind: z.literal("transferred"), transferTo: z.string().min(1) }).strict(),
]);
const AfterHoursBehaviourSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("closed_message"), phraseKey: z.string().min(1) }).strict(),
    z.object({ kind: z.literal("message_taken"), promptPhraseKey: z.string().min(1) }).strict(),
    z.object({ kind: z.literal("transferred"), transferTo: z.string().min(1) }).strict(),
]);
const ResolvedConnectionOperationSchema = z
    .object({
    method: z.string().min(1),
    pathTemplate: z.string().min(1),
    operationId: z.string().min(1),
})
    .strict();
/**
 * `secretRef` is a Key Vault secret NAME (design 3.6: `^[0-9a-zA-Z-]{1,127}$`). The schema
 * enforces the name shape, which also makes it structurally impossible to smuggle a secret
 * value through this field: a value would have to look like a Key Vault name to pass.
 */
export const ResolvedConnectionSchema = z
    .object({
    connectionId: idSchema(connectionId),
    baseUrl: z.string().regex(HTTPS_URL, "must be an absolute http(s) URL"),
    authMethod: z.string().min(1),
    secretRef: z.string().regex(/^[0-9a-zA-Z-]{1,127}$/, "not a Key Vault secret name"),
    operations: z.array(ResolvedConnectionOperationSchema),
})
    .strict();
/**
 * The consent line, keyed by medium and then by language (AMENDED 2026-09-22, P6-01, Issue 4b).
 *
 * All four media are REQUIRED keys, and `.strict()` rejects a fifth. That is deliberate: a
 * document that omits `whatsapp` is not "no WhatsApp consent line", it is a build that did not
 * decide, and the per-language `null` already spells the decision to have none. Because the
 * four keys are required, the pre-widening per-language-only document (`{"el": "...", "en":
 * "..."}`) fails to parse rather than being read as a medium map with stringly-typed media —
 * which is the loud failure design 12.4 wants while no `config_document` exists yet.
 */
const MEDIUM_KEY = MEDIUM;
const PerLanguageConsentLineSchema = z.record(LanguageCodeSchema, z.string().min(1).nullable());
const ConsentLineSchema = z
    .object({
    voice: PerLanguageConsentLineSchema,
    web_chat: PerLanguageConsentLineSchema,
    whatsapp: PerLanguageConsentLineSchema,
    viber: PerLanguageConsentLineSchema,
})
    .strict();
/** One line of the customer's own text: bounded, not blank, no control string (D-19). */
function singleLineText(maximum) {
    return z
        .string()
        .min(1)
        .max(maximum)
        .regex(SINGLE_LINE_WITHOUT_CONTROL_STRING, "must be one line without a control character, <| or |>")
        .regex(/\S/u, "must not be blank");
}
/** One pass target (plan-021 ED-3, design §33.4.2). The cross-entry rules are in the object's `superRefine` below. */
const VoiceAgentPassTargetSchema = z
    .object({
    targetKey: z.enum(VOICE_AGENT_PASS_TARGET_KEY),
    targetVoiceAgentId: idSchema(voiceAgentId),
    displayName: singleLineText(VOICE_AGENT_PASS_DISPLAY_NAME_CHARACTER_LIMIT),
    when: singleLineText(VOICE_AGENT_PASS_WHEN_CHARACTER_LIMIT),
    mode: z.enum(VOICE_AGENT_PASS_MODE),
    mayPassBack: z.boolean(),
    note: singleLineText(VOICE_AGENT_PASS_NOTE_CHARACTER_LIMIT).nullable(),
})
    .strict();
const VoiceAgentPassSchema = z
    .object({ target: z.array(VoiceAgentPassTargetSchema).max(VOICE_AGENT_PASS_TARGET_LIMIT) })
    .strict();
const RuntimeConfigObjectSchema = z
    .object({
    accountId: idSchema(accountId),
    workspaceId: idSchema(workspaceId),
    voiceAgentId: idSchema(voiceAgentId),
    agentVersionId: idSchema(agentVersionId),
    agentTemplateCode: z.string().min(1),
    languages: z.array(LanguageCodeSchema).min(1),
    voiceId: idSchema(voiceId),
    /*
     * ADDED 2026-10-09 (plan-015 R1, design §28.5.1, ADR 0042); REQUIRED since 2026-10-10 (G1C,
     * Release 3): every stored document carries it (migration 0121's back-fill, the G1W writers), so
     * the tolerant read is gone and a document without the map fails to parse like any other
     * unfinished build. It is checked below: complete over `languages`, opening entry = `voiceId`.
     */
    voiceByLanguage: z.record(LanguageCodeSchema, idSchema(voiceId)),
    greeting: z.string().min(1),
    concurrencyCeiling: z.number().int().positive(),
    extraction: z.unknown(),
    connections: z.array(ResolvedConnectionSchema),
    openingHours: OpeningHoursSchema,
    overflow: OverflowBehaviourSchema,
    afterHours: AfterHoursBehaviourSchema,
    consentLine: ConsentLineSchema,
    recordingEnabled: z.boolean(),
    answers: z
        .array(z.object({ question: z.string().min(1).max(500), answer: z.string().min(1).max(2000) }).strict())
        .max(200),
    transferTo: z.string().min(1).nullable(),
    prefixTokenCount: z.record(LanguageCodeSchema, z.number().int().positive()),
    /*
     * ADDED 2026-10-19 (plan-021 DOM, ADR 0074); REQUIRED since plan-021 CONTRACT (Release 2): every stored built document
     * carries it (migration 0168's back-fill, the factory and the api's derived drafts write it), so Release 1's tolerant
     * read is gone. `{ target: [] }` is a version that passes to no one; an absent key is refused.
     */
    voiceAgentPass: VoiceAgentPassSchema,
})
    .strict()
    .superRefine((value, ctx) => {
    // `extraction` is `unknown`, which zod would otherwise treat as an optional key. The
    // contract has no optional fields, so presence is asserted explicitly.
    if (!Object.hasOwn(value, "extraction")) {
        ctx.addIssue({ code: "custom", path: ["extraction"], message: "Required" });
    }
    // Every configured language must carry a consent-line decision and a measured prefix
    // token count. A missing key is not "no consent line" — it is an unfinished build.
    for (const language of value.languages) {
        for (const medium of MEDIUM_KEY) {
            if (!Object.hasOwn(value.consentLine[medium], language)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["consentLine", medium, language],
                    message: `consentLine.${medium} has no entry for configured language ${language}`,
                });
            }
        }
        if (!Object.hasOwn(value.prefixTokenCount, language)) {
            ctx.addIssue({
                code: "custom",
                path: ["prefixTokenCount", language],
                message: `prefixTokenCount has no entry for configured language ${language} (F-FAC-12)`,
            });
        }
    }
    // plan-015 R1 / G1C: the voice by language map names exactly the configured languages, and the
    // opening language's voice is `voiceId` (the column's copy). A map that misses a language is an
    // unfinished build, never "that language in some other voice".
    const voiceByLanguage = value.voiceByLanguage;
    for (const language of value.languages) {
        if (!Object.hasOwn(voiceByLanguage, language)) {
            ctx.addIssue({
                code: "custom",
                path: ["voiceByLanguage", language],
                message: `voiceByLanguage has no voice for configured language ${language}`,
            });
        }
    }
    for (const language of Object.keys(voiceByLanguage)) {
        if (!value.languages.includes(language)) {
            ctx.addIssue({
                code: "custom",
                path: ["voiceByLanguage", language],
                message: `voiceByLanguage names ${language}, which is not a configured language`,
            });
        }
    }
    const opening = value.languages[0];
    if (opening !== undefined && Object.hasOwn(voiceByLanguage, opening) && voiceByLanguage[opening] !== value.voiceId) {
        ctx.addIssue({
            code: "custom",
            path: ["voiceByLanguage", opening],
            message: `voiceByLanguage.${opening} must equal voiceId: the opening language's voice is voiceId`,
        });
    }
    if (new Set(value.languages).size !== value.languages.length) {
        ctx.addIssue({ code: "custom", path: ["languages"], message: "duplicate language" });
    }
    // plan-021 ED-3 (design §33.4.2): keys unique, each target named once and never this VoiceAgent itself, and a cold
    // pass carries no note (the target is told nothing).
    const seenKey = new Set();
    const seenTarget = new Set();
    value.voiceAgentPass.target.forEach((target, index) => {
        if (seenKey.has(target.targetKey)) {
            ctx.addIssue({
                code: "custom",
                path: ["voiceAgentPass", "target", index, "targetKey"],
                message: `duplicate pass target key ${target.targetKey}`,
            });
        }
        seenKey.add(target.targetKey);
        if (seenTarget.has(target.targetVoiceAgentId)) {
            ctx.addIssue({
                code: "custom",
                path: ["voiceAgentPass", "target", index, "targetVoiceAgentId"],
                message: "a VoiceAgent is listed as a pass target more than once",
            });
        }
        seenTarget.add(target.targetVoiceAgentId);
        if (target.targetVoiceAgentId === value.voiceAgentId) {
            ctx.addIssue({
                code: "custom",
                path: ["voiceAgentPass", "target", index, "targetVoiceAgentId"],
                message: "a VoiceAgent cannot pass to itself",
            });
        }
        if (target.mode === "cold" && target.note !== null) {
            ctx.addIssue({
                code: "custom",
                path: ["voiceAgentPass", "target", index, "note"],
                message: "a cold pass tells the target nothing: note must be null",
            });
        }
    });
    const operationIds = value.connections.flatMap((c) => c.operations.map((o) => o.operationId));
    if (new Set(operationIds).size !== operationIds.length) {
        ctx.addIssue({
            code: "custom",
            path: ["connections"],
            message: "duplicate operationId across the connection allowlist (AC-16)",
        });
    }
});
/**
 * The validator both sides of Seam A call. Parsing yields a **deep-frozen**
 * `AgentRuntimeConfig`: immutability is established at the boundary, not left to the caller.
 *
 * plan-015 G1C (Release 3): `voiceByLanguage` is required in the document. R1's tolerant read — a
 * document without the map read as every language in `voiceId` — is removed; such a document is
 * refused here, at the boundary, like any other unfinished build.
 *
 * plan-021 CONTRACT (Release 2, ADR 0074 decision 5): `voiceAgentPass` likewise. Release 1's transform that read a
 * document without the key as `{ target: [] }` is deleted; such a document is refused here.
 */
export const RuntimeConfigSchema = RuntimeConfigObjectSchema.transform((value) => 
// Safe: every field above is validated, `extraction` is asserted present, the map is checked
// complete, and the shape is structurally the contract. The cast exists only because zod widens
// `unknown` and does not brand the record's language keys.
deepFreezeRuntimeConfig(value));
//# sourceMappingURL=runtime-config.schema.js.map